"use client";

import { useMemo } from "react";
import {
  entrySiblings,
  intervalNameFR,
  type ChordEntry,
  type ChordQualityKey,
} from "@/lib/music/chord-catalog";
import { academicChordFingering } from "@/lib/music/academic-chord";
import { ROOT_PITCH_CLASS, pitchClassToName } from "@/lib/music/theory-helpers";
import { VIOLET_SOFT } from "./palette";
import { useStaticValidation } from "./use-static-validation";
import { ChordPracticeGridHeader } from "./chord-practice-grid-header";
import { ChordPracticeGridTheoryStrip } from "./chord-practice-grid-theory-strip";
import { ChordPracticeGridStage } from "./chord-practice-grid-stage";
import { ChordPracticeGridVariants } from "./chord-practice-grid-variants";
import type { ChordPracticeMode } from "./chord-practice-mode-toggle";

type Props = {
  entry: ChordEntry;
  /** Persistent set of validated inversion ids (controlled). */
  done?: ReadonlySet<string>;
  /** Called once when an inversion transitions from unvalidated to validated. */
  onMarkDone?: (invId: string) => void;
  mode?: ChordPracticeMode;
  onModeChange?: (mode: ChordPracticeMode) => void;
  onClose?: () => void;
  /** Called when the user picks a chord in the variants strip. If omitted,
   *  the strip falls back to a /studio/chords?practice=<id> router push. */
  onSelectVariant?: (entry: ChordEntry) => void;
  /** Hide the bottom variants strip. Used by the Harmony tab's chord
   *  detail, where the atlas already drives chord navigation. */
  hideVariants?: boolean;
};

const VARIANT_QUALITIES: ChordQualityKey[] = [
  "major",
  "minor",
  "diminished",
  "augmented",
  "sus2",
  "sus4",
  "7",
  "maj7",
];

const INVERSION_TITLES = [
  "Position fondamentale",
  "1er renversement",
  "2e renversement",
  "3e renversement",
];

export function ChordPracticeGrid({
  entry,
  done,
  onMarkDone,
  mode = "static",
  onModeChange,
  onClose,
  onSelectVariant,
  hideVariants = false,
}: Props) {
  const rootPc = useMemo(() => ROOT_PITCH_CLASS[entry.root], [entry.root]);
  const numNotes = entry.intervals.length;

  const validation = useStaticValidation({ entry, done, onMarkDone });
  const { effInv, maxInvIdx, invertedSemis, requiredPcs } = validation;

  const noteNames = useMemo(
    () =>
      invertedSemis.map((s) => {
        const pc = (((rootPc + s) % 12) + 12) % 12;

        return pitchClassToName(pc, entry.root);
      }),
    [invertedSemis, rootPc, entry.root],
  );

  const slashChord = useMemo(
    () =>
      effInv === 0 ? entry.symbol : `${entry.symbol}/${noteNames[0]}`,
    [effInv, entry.symbol, noteNames],
  );

  const fingeringsRight = useMemo<number[]>(
    () => academicChordFingering(numNotes, "right"),
    [numNotes],
  );

  const fingeringsLeft = useMemo<number[]>(
    () => academicChordFingering(numNotes, "left"),
    [numNotes],
  );

  const intervalsFromBass = useMemo(() => {
    const out: { from: string; to: string; name: string }[] = [];
    for (let i = 1; i < invertedSemis.length; i++) {
      out.push({
        from: noteNames[0],
        to: noteNames[i],
        name: intervalNameFR(invertedSemis[i] - invertedSemis[0]),
      });
    }

    return out;
  }, [invertedSemis, noteNames]);

  const variants = useMemo(() => {
    const siblings = entrySiblings(entry);

    return VARIANT_QUALITIES.map((q) => siblings.find((s) => s.quality === q))
      .filter((s): s is ChordEntry => !!s);
  }, [entry]);

  const inversionTitle =
    INVERSION_TITLES[effInv] ?? `${effInv}e renversement`;

  const prevIdx = effInv > 0 ? effInv - 1 : maxInvIdx;
  const nextIdx = effInv < maxInvIdx ? effInv + 1 : 0;
  const prevTitle =
    INVERSION_TITLES[prevIdx] ?? `${prevIdx}e renversement`;
  const nextTitle =
    INVERSION_TITLES[nextIdx] ?? `${nextIdx}e renversement`;

  const allDone = validation.done.size > maxInvIdx;

  return (
    <div className="relative flex h-full flex-col">
      {/* Ambient violet glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(ellipse at 50% 50%, ${VIOLET_SOFT} 0%, transparent 55%)`,
        }}
      />

      <ChordPracticeGridHeader
        entry={entry}
        effInv={effInv}
        maxInvIdx={maxInvIdx}
        inversionTitle={inversionTitle}
        mode={mode}
        onModeChange={onModeChange}
        onClose={onClose}
      />

      <ChordPracticeGridTheoryStrip
        entry={entry}
        inversionTitle={inversionTitle}
        slashChord={slashChord}
        bassName={noteNames[0]}
        inversionIndex={effInv}
        intervalsFromBass={intervalsFromBass}
      />

      <ChordPracticeGridStage
        positionId={validation.positionId}
        noteNames={noteNames}
        numNotes={numNotes}
        rootPc={rootPc}
        requiredPcs={requiredPcs}
        fingeringsRight={fingeringsRight}
        fingeringsLeft={fingeringsLeft}
        activePcs={validation.activePcs}
        justValidated={validation.justValidated}
        allDone={allDone}
        onSwitchToTempo={
          onModeChange ? () => onModeChange("tempo") : undefined
        }
        maxInvIdx={maxInvIdx}
        prevTitle={prevTitle}
        nextTitle={nextTitle}
        onPrev={validation.goPrev}
        onNext={validation.goNext}
        onListen={validation.handleListen}
        onRandom={validation.handleRandom}
      />

      {!hideVariants && (
        <ChordPracticeGridVariants
          entry={entry}
          variants={variants}
          onSelect={onSelectVariant}
        />
      )}

      <style>{`
        @keyframes chordPracticePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.4); }
        }
        .chord-practice-variants {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .chord-practice-variants::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
